import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, ArrowRight, Users, MapPin, Sparkles, Volume2, Move, Shuffle, MessageSquare, Zap, RotateCcw, Palette } from "lucide-react";
import { THEMES, ModeToggle } from "../context/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CreateSession = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    session_mode: "hybrid", // hybrid (both quiz & carousel), carousel, or quiz
    theme: "reshapers", // reshapers, teambuilding, marcom
    num_groups: 4,
    num_stations: 4,
    energizer_mode: "sound",
    timer_duration: 300,
    show_questions: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Please enter a session name");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/sessions`, formData);
      const session = response.data.session;
      toast.success("Session created successfully!");
      navigate(`/facilitator/${session.id}`);
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error("Failed to create session");
    } finally {
      setIsLoading(false);
    }
  };

  const sessionModes = [
    { 
      value: "hybrid", 
      label: "Full Workshop", 
      icon: Sparkles, 
      description: "Quiz rounds + Station rotations - do both!",
      color: "from-[#8B5CF6] to-[#E91E8C]"
    },
    { 
      value: "carousel", 
      label: "Stations Only", 
      icon: RotateCcw, 
      description: "Rotations & discussion questions only",
      color: "from-[#E91E8C] to-[#8B5CF6]"
    },
    { 
      value: "quiz", 
      label: "Quiz Only", 
      icon: Zap, 
      description: "Team buzzer quiz competition only",
      color: "from-[#FF1744] to-[#FF9100]"
    }
  ];

  const energizerModes = [
    { value: "sound", label: "Sound Signals", icon: Volume2, description: "Animal sounds and noises" },
    { value: "movement", label: "Movement Signals", icon: Move, description: "Physical actions and poses" },
    { value: "mixed", label: "Mixed Signals", icon: Shuffle, description: "Both sounds and movements" }
  ];

  const timerOptions = [
    { value: 180, label: "3 minutes" },
    { value: 300, label: "5 minutes" },
    { value: 420, label: "7 minutes" },
    { value: 600, label: "10 minutes" },
    { value: 900, label: "15 minutes" }
  ];

  const showStationConfig = formData.session_mode === "carousel" || formData.session_mode === "hybrid";
  const showQuizNote = formData.session_mode === "quiz" || formData.session_mode === "hybrid";

  return (
    <div className="min-h-screen p-6 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#E91E8C] rounded-full opacity-10 blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#8B5CF6] rounded-full opacity-10 blur-[150px]" />

      {/* Dark/Light Mode Toggle */}
      <div className="absolute top-4 right-4 z-20">
        <ModeToggle />
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#E2D9F3] hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold gradient-text mb-2">Create Session</h1>
          <p className="text-[#E2D9F3]">Set up your workshop or quiz</p>
        </motion.div>

        {/* Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Session Mode Selection */}
          <div className="glass-heavy rounded-2xl p-6">
            <Label className="text-lg font-semibold text-white mb-4 block">Session Type</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {sessionModes.map((mode) => {
                const Icon = mode.icon;
                const isSelected = formData.session_mode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    data-testid={`session-mode-${mode.value}`}
                    onClick={() => setFormData({ ...formData, session_mode: mode.value })}
                    className={`p-5 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? "border-white/50 bg-gradient-to-br " + mode.color + " bg-opacity-20"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                      isSelected ? "bg-white/20" : "bg-white/10"
                    }`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <p className="font-bold text-white text-lg">{mode.label}</p>
                    <p className="text-sm text-[#E2D9F3] mt-1">{mode.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Theme Selection */}
          <div className="glass-heavy rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <div>
                <Label className="text-lg font-semibold text-white">Color Theme</Label>
                <p className="text-sm text-[#E2D9F3]">Choose your brand colors</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.entries(THEMES).map(([key, theme]) => {
                const isSelected = formData.theme === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({ ...formData, theme: key })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      isSelected 
                        ? 'border-white bg-white/15' 
                        : 'border-white/10 bg-white/5 hover:border-white/30 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      {/* Color swatches */}
                      <div className="flex -space-x-1">
                        {theme.preview.map((color, idx) => (
                          <div
                            key={idx}
                            className="w-7 h-7 rounded-full border-2 border-[#0D0118]"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <span className={`font-bold ${isSelected ? 'text-white' : 'text-[#E2D9F3]'}`}>
                        {theme.name}
                      </span>
                    </div>
                    <p className="text-xs text-[#6B5E7C]">{theme.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Session Name */}
          <div className="glass-heavy rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <Label className="text-lg font-semibold text-white">Session Name</Label>
                <p className="text-sm text-[#E2D9F3]">Give your session a name</p>
              </div>
            </div>
            <Input
              data-testid="session-name-input"
              type="text"
              placeholder="e.g., Q1 Team Workshop"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input-dark text-lg"
            />
          </div>

          {/* Groups - Always shown */}
          <div className="glass-heavy rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#8B5CF6] flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <Label className="text-lg font-semibold text-white">Number of Teams</Label>
                <p className="text-sm text-[#E2D9F3]">How many teams are competing?</p>
              </div>
            </div>
            <Input
              data-testid="num-groups-input"
              type="number"
              min={1}
              max={100}
              value={formData.num_groups}
              onChange={(e) => setFormData({ ...formData, num_groups: Math.max(1, parseInt(e.target.value) || 1) })}
              className="input-dark"
            />
          </div>

          {/* Stations - For Carousel and Hybrid modes */}
          {showStationConfig && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="glass-heavy rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#E91E8C] flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <Label className="text-lg font-semibold text-white">Number of Stations</Label>
                  <p className="text-sm text-[#E2D9F3]">How many stops in the carousel?</p>
                </div>
              </div>
              <Input
                data-testid="num-stations-input"
                type="number"
                min={1}
                max={100}
                value={formData.num_stations}
                onChange={(e) => setFormData({ ...formData, num_stations: Math.max(1, parseInt(e.target.value) || 1) })}
                className="input-dark"
              />
            </motion.div>
          )}

          {/* Energizer Mode - Always shown */}
          <div className="glass-heavy rounded-2xl p-6">
            <Label className="text-lg font-semibold text-white mb-4 block">Energizer Signal Type</Label>
            <p className="text-sm text-[#E2D9F3] mb-4">How participants will find their team</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {energizerModes.map((mode) => {
                const Icon = mode.icon;
                const isSelected = formData.energizer_mode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    data-testid={`energizer-mode-${mode.value}`}
                    onClick={() => setFormData({ ...formData, energizer_mode: mode.value })}
                    className={`p-4 rounded-xl border transition-all text-left ${
                      isSelected
                        ? "border-[#E91E8C] bg-[#E91E8C]/20"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${isSelected ? "text-[#E91E8C]" : "text-[#E2D9F3]"}`} />
                    <p className="font-semibold text-white">{mode.label}</p>
                    <p className="text-xs text-[#E2D9F3] mt-1">{mode.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Timer Duration - For Carousel and Hybrid modes */}
          {showStationConfig && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="glass-heavy rounded-2xl p-6"
            >
              <Label className="text-lg font-semibold text-white mb-4 block">Station Duration</Label>
              <p className="text-sm text-[#E2D9F3] mb-4">Time at each station before rotation</p>
              <Select
                value={formData.timer_duration.toString()}
                onValueChange={(value) => setFormData({ ...formData, timer_duration: parseInt(value) })}
              >
                <SelectTrigger data-testid="timer-duration-select" className="input-dark">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timerOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#6B5E7C] mt-2">Timer is optional - you can rotate manually anytime</p>
            </motion.div>
          )}

          {/* Show Questions Toggle - For Carousel and Hybrid modes */}
          {showStationConfig && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="glass-heavy rounded-2xl p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00B0FF] flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <Label className="text-lg font-semibold text-white">Show Discussion Questions</Label>
                    <p className="text-sm text-[#E2D9F3]">Display questions at each station</p>
                  </div>
                </div>
                <Switch
                  data-testid="show-questions-toggle"
                  checked={formData.show_questions}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_questions: checked })}
                />
              </div>
            </motion.div>
          )}

          {/* Quiz Note - For Quiz and Hybrid modes */}
          {showQuizNote && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="glass-light rounded-2xl p-4 border border-[#8B5CF6]/30"
            >
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-[#CCFF00]" />
                <p className="text-sm text-[#E2D9F3]">
                  You can add quiz questions after creating the session in the facilitator dashboard.
                </p>
              </div>
            </motion.div>
          )}

          {/* Submit */}
          <Button
            data-testid="create-session-submit"
            type="submit"
            disabled={isLoading}
            className="w-full btn-action control-btn text-lg py-6"
          >
            {isLoading ? "Creating..." : `Create Session`}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.form>
      </div>
    </div>
  );
};

export default CreateSession;
